import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Checkbox,
  Flex,
  Skeleton,
  Spacer,
  Text,
  Tooltip,
  useToast,
  VStack,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';

import { finleyAssert } from '@finley-technologies/common';

import {
  AthenaTableGQLType,
  AthenaTablePropertyGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import { useUserAuthOrThrow } from '../../../contexts/UserAuthProvider';
import {
  athenaDataTypeToIcon,
  filterAthenaTablesForCreditFacilities,
  filterAthenaTablesRelevantToSqlGeneration,
} from './utils';
import { useGetAthenaTablesForOrganizationQuery } from 'packages/athena/requests/requests.gql.generated';
import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import {
  doesSearchQueryExistInString,
  SearchBar,
} from 'packages/common-frontend/components/SearchAndCopyComponents';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { useGetCreditFacilitiesByOrganizationQuery } from '~/contexts/CreditFacilitiesProvider/requests/getCreditFacilities.gql.generated';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';

const AthenaSchemaSelectorLoadingSkeleton = () => {
  return (
    <Box
      borderRadius="8px"
      borderWidth="1px"
      borderColor={colors.gray[200]}
      overflow="auto"
      backgroundColor="white"
    >
      <Box position="sticky" top="0">
        <SearchBar query="" onChangeQuery={() => {}} isDisabled />
      </Box>
      <VStack spacing="8px" alignItems="left" p="0 16px">
        {_.times(8, () => (
          <Skeleton h="30px" />
        ))}
      </VStack>
    </Box>
  );
};

export const AthenaTableSchemaSelectorAccordionPanel = ({
  athenaTable,
}: {
  athenaTable: AthenaTableGQLType;
}) => {
  return (
    <AccordionPanel py="0">
      <VStack spacing="8px" alignItems="left">
        {_.map(athenaTable.properties, (property) => {
          const leftIcon = athenaDataTypeToIcon[property.type]
            ? {
                iconName: athenaDataTypeToIcon[property.type],
                tooltip: property.type,
              }
            : undefined;
          return (
            <Flex
              key={`${athenaTable.tableName}-${property.name}`}
              alignItems="center"
            >
              {leftIcon && (
                <Tooltip label={leftIcon.tooltip}>
                  <Box alignSelf="normal">
                    <Icon
                      icon={leftIcon.iconName}
                      color={colors.black[70]}
                      style={{ marginRight: '8px' }}
                    />
                  </Box>
                </Tooltip>
              )}

              <Text
                fontSize="14px"
                lineHeight="28px"
                color={colors.black[70]}
                mr="8px"
              >
                {property.name}
              </Text>
            </Flex>
          );
        })}
      </VStack>
    </AccordionPanel>
  );
};

const AthenaSchemaSelectorBody = ({
  athenaTables,
  onToggleTableSelect,
  selectedTableNames,
}: {
  athenaTables: AthenaTableGQLType[];
  onToggleTableSelect: (tableInput: AthenaTableGQLType) => void;
  selectedTableNames: string[];
}) => {
  const [query, setQuery] = useState<string>('');
  const [openAccordionIndices, setOpenAccordionIndices] = useState<
    number | number[]
  >([]);

  const filteredAthenaTableSchema = useMemo(() => {
    const filteredAthenaTables: AthenaTableGQLType[] = [];
    _.forEach(athenaTables, (athenaTable) => {
      // if the tableName itself is in the search query, add table schema with all its properties
      if (
        doesSearchQueryExistInString({
          queryString: query,
          stringToEvaluate: athenaTable.tableName,
        })
      ) {
        filteredAthenaTables.push(athenaTable);
      } else {
        const filteredPropertiesForTable: AthenaTablePropertyGQLType[] = [];

        // if the tableName itself doesn't match the search query
        // check each property individually
        _.forEach(athenaTable.properties, (property) => {
          if (
            doesSearchQueryExistInString({
              queryString: query,
              stringToEvaluate: property.name,
            })
          ) {
            filteredPropertiesForTable.push(property);
          }
        });

        // if any properties matched the query term, add the table with the filtered properties
        if (filteredPropertiesForTable.length) {
          filteredAthenaTables.push({
            tableName: athenaTable.tableName,
            properties: filteredPropertiesForTable,
          });
        }
      }
    });

    return filteredAthenaTables;
  }, [athenaTables, query]);

  const onChangeQuery = useCallback(
    (newQuery: string) => {
      setQuery(newQuery);

      if (!newQuery) {
        setOpenAccordionIndices([]);
      } else {
        const numberOfFilteredGroups = athenaTables.length;
        setOpenAccordionIndices([...Array(numberOfFilteredGroups).keys()]);
      }
    },
    [athenaTables],
  );

  const doAthenaTablesExist = !!athenaTables.length;

  return (
    <Box>
      <Box position="sticky" top="0">
        <SearchBar
          query={query}
          onChangeQuery={onChangeQuery}
          isDisabled={!doAthenaTablesExist}
          backgroundColor={colors.background[50]}
        />
      </Box>
      {doAthenaTablesExist ? (
        <Accordion
          allowMultiple
          index={openAccordionIndices}
          onChange={(openIndices) => setOpenAccordionIndices(openIndices)}
          overflow="auto"
        >
          {_.map(filteredAthenaTableSchema, (athenaTable) => {
            const isSelected = selectedTableNames.some(
              (tableName) => tableName === athenaTable.tableName,
            );

            return (
              <AccordionItem
                border="0"
                key={`accordion-item-${athenaTable.tableName}`}
              >
                <AccordionButton width="100%">
                  <Checkbox
                    type="checkbox"
                    isChecked={isSelected}
                    onChange={() => onToggleTableSelect(athenaTable)}
                    mr="2"
                    backgroundColor="white"
                  />
                  <Text fontWeight={500}>{athenaTable.tableName}</Text>

                  <Spacer />
                  <AccordionIcon color={colors.black[95]} />
                </AccordionButton>
                <Box ml="24px">
                  <AthenaTableSchemaSelectorAccordionPanel
                    athenaTable={athenaTable}
                  />
                </Box>
              </AccordionItem>
            );
          })}
        </Accordion>
      ) : (
        <Text>No available data tables to use for inputs.</Text>
      )}
    </Box>
  );
};

/**
 * Can be used on Borrower or Lender dashboard.
 */
export const AthenaSchemaSelector = ({
  creditFacilityId,
  onToggleTableSelect,
  selectedTableNames,
  mode,
}: {
  creditFacilityId?: string; // pass in if filtering table schema by credit facility, from organization
  onToggleTableSelect: (tableInput: AthenaTableGQLType) => void;
  selectedTableNames: string[];
  mode: 'allCreditFacilityTables' | 'relevantAthenaSqlGenerationTables';
}) => {
  const flags = useFeatureFlags();
  const toast = useToast();
  const user = useUserAuthOrThrow();

  const {
    data: creditFacilitiesData,
    loading: isCreditFacilitiesDataLoading,
  } = useGetCreditFacilitiesByOrganizationQuery({
    variables: {
      organizationId: user.user.organizationId, // update to pass in specific organization id if using this component on admin
      creditFacilitySlugsToHide: flags.creditFacilitySlugsToHide ?? [],
    },
  });

  const {
    data: athenaTablesData,
    loading: isAthenaTablesDataLoading,
  } = useGetAthenaTablesForOrganizationQuery({
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: { organizationId: user.user.organizationId }, // update to pass in specific organization id if using this component on admin
  });

  const filteredAthenaTables = useMemo(() => {
    if (!athenaTablesData?.athenaTables) {
      return [];
    }

    let filteredTables: AthenaTableGQLType[] = athenaTablesData.athenaTables;

    // filter by creditFacility first if passed in
    if (
      creditFacilityId &&
      creditFacilitiesData?.organization.creditFacilities.length
    ) {
      const matchingCreditFacility = creditFacilitiesData.organization.creditFacilities.find(
        (creditFacility) => creditFacility.id === creditFacilityId,
      );

      finleyAssert(
        matchingCreditFacility,
        `matchingCreditFacility must exist for [creditFacilityId: ${creditFacilityId}]`,
      );

      filteredTables = filterAthenaTablesForCreditFacilities({
        athenaTables: athenaTablesData.athenaTables,
        creditFacilitySlugs: [matchingCreditFacility.slug],
      });
    }

    // filter for relevant tables for athenaSqlGeneration
    if (mode === 'relevantAthenaSqlGenerationTables') {
      filteredTables = filterAthenaTablesRelevantToSqlGeneration({
        athenaTables: filteredTables,
      });
    }

    return filteredTables;
  }, [
    athenaTablesData?.athenaTables,
    creditFacilitiesData?.organization.creditFacilities,
    creditFacilityId,
    mode,
  ]);

  if (
    !athenaTablesData ||
    isAthenaTablesDataLoading ||
    !creditFacilitiesData ||
    isCreditFacilitiesDataLoading
  ) {
    return <AthenaSchemaSelectorLoadingSkeleton />;
  }

  return (
    <Box
      borderRadius="8px"
      borderWidth="1px"
      borderColor={colors.gray[200]}
      overflow="auto"
      backgroundColor={colors.background[50]}
      pb="16px"
    >
      <AthenaSchemaSelectorBody
        athenaTables={filteredAthenaTables}
        onToggleTableSelect={onToggleTableSelect}
        selectedTableNames={selectedTableNames}
      />
    </Box>
  );
};
